import type { InteractionDirection, InteractionKind } from '@/api/types'
import { INTERACTION_KIND_ORDER } from '@/lib/orders'
import { exactly, integer, matching, oneOf, readSearch, text } from '@/lib/search-params'

/**
 * Filtres de l'onglet Échanges, **à part** du reste de `lib/interaction` : la
 * route de la fiche client les déclare, et tout ce qu'elle importe entre dans
 * l'entrée de l'application. Le formatage de dates (date-fns et sa locale)
 * n'a rien à y faire — l'y entraîner coûtait 10 ko compressés.
 */

/** Échanges par page dans une liste. */
export const INTERACTIONS_PAGE_SIZE = 25

const DIRECTIONS = ['entrant', 'sortant'] as const

/**
 * Filtres de l'onglet Échanges, dans l'URL : « les appels avec Alice ce
 * mois-ci » se partage et survit à un rechargement. Préfixés `ech` parce
 * qu'ils cohabitent avec ceux des autres onglets de la fiche client.
 */
export const interactionsShape = {
  /** Types séparés par des virgules. */
  echType: text(),
  echSens: oneOf(DIRECTIONS),
  echContact: text(),
  echAffaire: text(),
  /** `me` : mes échanges. */
  echAuteur: exactly('me' as const),
  echDu: text(),
  echAu: text(),
  echQ: text(),
  echPage: integer({ min: 1 }),
  /** Lot 10 : `semaine` affiche le calendrier ; la liste à défaut. */
  echVue: oneOf(['semaine'] as const),
  /** Un jour de la semaine affichée. */
  echSemaine: matching(/^\d{4}-\d{2}-\d{2}$/),
}

export function parseInteractionsSearch(search: Record<string, unknown>): InteractionsSearch {
  return readSearch(interactionsShape, search)
}

export interface InteractionsSearch {
  echType?: string | undefined
  echSens?: InteractionDirection | undefined
  echContact?: string | undefined
  echAffaire?: string | undefined
  echAuteur?: 'me' | undefined
  echDu?: string | undefined
  echAu?: string | undefined
  echQ?: string | undefined
  echPage?: number | undefined
  echVue?: 'semaine' | undefined
  echSemaine?: string | undefined
}

export const EMPTY_INTERACTION_FILTERS = {
  echType: undefined,
  echSens: undefined,
  echContact: undefined,
  echAffaire: undefined,
  echAuteur: undefined,
  echDu: undefined,
  echAu: undefined,
  echQ: undefined,
  echPage: undefined,
} satisfies InteractionsSearch

export const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/** Types lisibles de l'URL, dans l'ordre d'affichage ; l'inconnu est écarté. */
export function parseKinds(value: string | undefined): InteractionKind[] {
  if (!value) return []
  const wanted = value.split(',').map((item) => item.trim())
  return INTERACTION_KIND_ORDER.filter((kind) => wanted.includes(kind))
}

export function hasActiveInteractionFilters(search: InteractionsSearch): boolean {
  return !!(
    search.echType ??
    search.echSens ??
    search.echContact ??
    search.echAffaire ??
    search.echAuteur ??
    search.echDu ??
    search.echAu ??
    (search.echQ?.trim() ? search.echQ : undefined)
  )
}
